/**
 * Summary Card Component for Admin Control Panel
 * 
 * Clickable card displaying a metric with optional trend indicator.
 */
import React from "react";
import { TrendingUp, TrendingDown, Minus } from "lucide-react";
import { Card, CardContent } from "@/components/ui/card";

const SummaryCard = ({
  title,
  value,
  icon: Icon,
  iconBgColor = "bg-slate-800",
  iconColor = "text-slate-300",
  description,
  trend,
  trendLabel,
  onClick,
  testId
}) => {
  const formatNumber = (num) => {
    return new Intl.NumberFormat('en-US').format(num);
  };

  const getTrendIcon = (direction) => {
    switch (direction) {
      case 'up':
        return <TrendingUp className="w-3 h-3" />;
      case 'down':
        return <TrendingDown className="w-3 h-3" />;
      default:
        return <Minus className="w-3 h-3" />;
    }
  };

  const getTrendColor = (direction) => {
    switch (direction) {
      case 'up':
        return 'text-emerald-400';
      case 'down':
        return 'text-red-400';
      default:
        return 'text-slate-400';
    }
  };

  return (
    <Card
      className="bg-slate-900/60 border-slate-700 hover:border-slate-600 hover:bg-slate-800/60 transition-all cursor-pointer group"
      onClick={onClick}
      data-testid={testId}
    >
      <CardContent className="p-5">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            {/* Title */}
            <p className="text-sm font-medium text-slate-400 uppercase tracking-wide">
              {title}
            </p>
            
            {/* Value */}
            <p className="text-3xl font-bold text-white mt-2">
              {formatNumber(value)}
            </p>
            
            {/* Trend or Description */}
            {trend && (
              <div className={`flex items-center gap-1 mt-2 ${getTrendColor(trend.direction)}`}>
                {getTrendIcon(trend.direction)}
                <span className="text-sm font-medium">
                  {trend.value}%
                </span>
                {trendLabel && (
                  <span className="text-xs text-slate-500 ml-1">
                    {trendLabel}
                  </span>
                )}
              </div>
            )}
            
            {description && !trend && (
              <p className="text-xs text-slate-500 mt-2">
                {description}
              </p>
            )}
          </div>
          
          {/* Icon */}
          <div className={`p-3 rounded-lg ${iconBgColor} group-hover:scale-105 transition-transform`}>
            <Icon className={`w-6 h-6 ${iconColor}`} />
          </div>
        </div>

        {/* Click hint */}
        <div className="mt-4 pt-3 border-t border-slate-800">
          <p className="text-xs text-slate-500 group-hover:text-blue-400 transition-colors">
            Click to view details
          </p>
        </div>
      </CardContent>
    </Card>
  );
};

export default SummaryCard;
